import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import PageHero from "@/components/PageHero";

const HERO_IMG = "https://images.unsplash.com/photo-1527515637462-cff94eecc1ac?auto=format&fit=crop&w=2000&q=80";

const GROUPS = [
  {
    label: "Pricing & Estimates",
    items: [
      {
        q: "What is your pricing structure?",
        a: "Our professional cleaning services start at $0.12 per square foot. Final pricing depends on the size of the property, its condition at the time of service, the scope of work, and how frequently cleaning is scheduled. We provide free, itemized estimates and do not charge hidden fees.",
      },
      {
        q: "Can you match my current cleaning rate?",
        a: "We are often able to. If you are comfortable sharing what you currently pay for cleaning services, we will take an honest look and let you know whether we can offer a competitive or more cost-effective arrangement. Our priority is to earn your business with quality and reliability.",
      },
      {
        q: "How do I get an estimate?",
        a: "You can request an estimate by calling us, emailing us, or filling out the contact form on our website. For commercial properties, we may schedule a brief walkthrough to ensure the most accurate quote. Estimates are free and come with no obligation.",
      },
    ],
  },
  {
    label: "Commercial Cleaning",
    items: [
      {
        q: "Do you offer commercial cleaning contracts?",
        a: "Yes. We work with offices, commercial properties, and property management companies on recurring service agreements. We are happy to discuss a customized commercial cleaning program that fits your facility, schedule, and budget.",
      },
      {
        q: "How often should a commercial office be cleaned?",
        a: "This depends on the size of the office, the number of employees, and the nature of the business. Many office environments benefit from weekly or bi-weekly professional cleaning. High-traffic spaces may require more frequent service. We are happy to recommend a schedule based on a walkthrough of your facility.",
      },
      {
        q: "Do you serve property management companies?",
        a: "Yes. We regularly work with property management firms, landlords, and apartment building owners to provide turnover cleaning, post-tenant cleaning, move-in preparation, and ongoing maintenance cleaning across residential portfolios.",
      },
    ],
  },
  {
    label: "Residential & Special Services",
    items: [
      {
        q: "What is included in a deep cleaning?",
        a: "A deep cleaning goes beyond standard maintenance to address areas that are often overlooked: oven and refrigerator interiors, cabinet and drawer interiors, ceiling fans, light fixtures, baseboards, window sills, wall washing, and behind appliances. It is a thorough, top-to-bottom service designed to restore a space to its best condition.",
      },
      {
        q: "Do you offer move-in and move-out cleaning?",
        a: "Yes. Move-in and move-out cleaning is one of our most requested services. We provide detailed, top-to-bottom cleaning to help landlords turn units quickly, protect security deposits, and ensure properties are truly ready for the next occupant.",
      },
      {
        q: "Do you clean Airbnb and VRBO properties?",
        a: "Absolutely. We provide specialized turnover cleaning for short-term rental properties, including full cleaning of all rooms, linen reset, surface sanitization, kitchen and bathroom scrubbing, and a final review to confirm the property is guest-ready before your next booking.",
      },
      {
        q: "Do you offer recurring cleaning schedules?",
        a: "Yes. We offer weekly, bi-weekly, and monthly recurring cleaning plans for both residential and commercial properties. Recurring clients benefit from consistent scheduling, a familiar team, and often preferred pricing.",
      },
      {
        q: "Can you create a custom cleaning plan?",
        a: "Yes. We understand that every property is different. We are happy to design a customized cleaning scope that addresses the specific areas and priorities that matter most to you or your business.",
      },
    ],
  },
  {
    label: "Booking & Logistics",
    items: [
      {
        q: "What areas do you serve?",
        a: "We are based in Royal Oak, Michigan, and serve clients throughout Metro Detroit and Oakland County. For specific service area questions, please contact us directly.",
      },
      {
        q: "Do you bring your own supplies and equipment?",
        a: "Please contact us to discuss supply preferences for your specific engagement. We can accommodate client-supplied products for clients who have preferred brands or require specific cleaning agents for their facility.",
      },
      {
        q: "How far in advance should I book?",
        a: "We recommend booking as early as possible, especially for move-in/move-out cleans or recurring services. For one-time or urgent requests, please contact us and we will do our best to accommodate your timeline.",
      },
      {
        q: "How do I know the work will be done right?",
        a: "Eco Ready Solutions, LLC is built on more than 30 years of professional cleaning experience and a genuine commitment to quality. We follow detailed cleaning checklists, communicate openly with our clients, and stand behind our work. If something does not meet your expectations, let us know and we will make it right.",
      },
    ],
  },
];

export default function Faq() {
  useEffect(() => { document.title = "FAQ | Eco Ready Solutions, LLC"; }, []);

  return (
    <div data-testid="page-faq">
      <PageHero
        eyebrow="FAQ"
        title="Frequently Asked Questions"
        subtitle="Everything you need to know about working with Eco Ready Solutions, LLC."
        image={HERO_IMG}
      />

      <section className="eco-section bg-white">
        <div className="eco-container max-w-4xl">
          {GROUPS.map((g, gi) => (
            <div key={g.label} className={gi > 0 ? "mt-12" : ""}>
              <div className="flex items-baseline gap-3 mb-5">
                <span className="eco-eyebrow">Group {gi + 1}</span>
                <h2 className="text-[22px] sm:text-[26px] font-bold text-[#1A1A1A]">{g.label}</h2>
              </div>
              <Accordion type="single" collapsible className="w-full">
                {g.items.map((it, i) => (
                  <AccordionItem
                    key={i}
                    value={`g${gi}-i${i}`}
                    data-testid={`faq-item-${gi}-${i}`}
                    className="border-b border-[#D6DDD9]"
                  >
                    <AccordionTrigger className="text-left text-[16px] md:text-[17px] font-semibold text-[#1A1A1A] py-5 hover:no-underline hover:text-[#2D6A4F]">
                      {it.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[15.5px] text-[#555555] leading-[1.75] pb-5">
                      {it.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </section>

      <section className="eco-section" style={{ background: "#F4F6F5" }}>
        <div className="eco-container text-center max-w-3xl mx-auto">
          <h2 className="eco-h2">Still have questions?</h2>
          <p className="mt-4 text-[17px] text-[#555555]">We are happy to help.</p>
          <Link to="/contact" data-testid="faq-cta-contact" className="eco-btn eco-btn-primary mt-7">
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
}
